import { useEffect, useState } from 'react';

export const LayoutSizes = {
  mobile: 'mobile',
  tablet: 'tablet',
  desktop: 'desktop',
  largeDesktop: 'largeDesktop',
} as const;

type LayoutSize = typeof LayoutSizes[keyof typeof LayoutSizes];

const MIN_MAX_WIDTHS: Record<LayoutSize, [number, number]> = {
  [LayoutSizes.mobile]: [0, 768],
  [LayoutSizes.tablet]: [768, 992],
  [LayoutSizes.desktop]: [768, 992],
  [LayoutSizes.largeDesktop]: [1920, Number.MAX_SAFE_INTEGER],
};

function responsiveLayoutSize() {
  const width = window?.innerWidth;

  if (width === undefined) return null;

  return Object.keys(MIN_MAX_WIDTHS).find((size) => {
    const [min, max] = MIN_MAX_WIDTHS[size as LayoutSize];

    return width >= min && width < max;
  });
}

function useIsLayoutSize(
  size: LayoutSize,
  { skipResizeListener = false } = {},
) {
  const [currentSize, setCurrentSize] = useState(responsiveLayoutSize());

  useEffect(() => {
    if (skipResizeListener) return undefined;

    const handleResize = () => {
      const newCurrentSize = responsiveLayoutSize();
      if (newCurrentSize !== currentSize) {
        setCurrentSize(newCurrentSize);
      }
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => window.removeEventListener('resize', handleResize);
  }, [currentSize, skipResizeListener]);

  return size === currentSize;
}

export default useIsLayoutSize;
